import React from "react";
import { Button } from "../..";
import { Modal } from "react-bootstrap";
import { ProjectDetailsForm } from "./components/ProjectDetailsForm";
import { useCreateProject } from "./hooks/useCreateProject";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { FormProvider, useForm } from "react-hook-form";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";
import { CreateProjectModalForm, CreateProjectModalProps, CreateProjectModalResponse, OpenCreateProjectModalOptions } from "./types";
import ThemedModal from "../../utilities/ThemedModal";

const CreateProjectModal = ({
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    dialogId,
    dismiss,
    masterAccountId,
    proceed,
    show,
    size = "lg",
    ...rest
} : Readonly<CreateProjectModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const formMethods = useForm<CreateProjectModalForm>();
    const { handleSubmit, reset } = formMethods;
    const { create, isLoading: isCreating } = useCreateProject(masterAccountId);

    const onClose = () => cancel!();

    const onSubmit = (form: CreateProjectModalForm) => {
        create(form).then((r) => {
            if (r.status < 300) {
                proceed!(r);
            }
        });
    };

    React.useEffect(() => {
        if (!defaultValues) {
            return;
        }
        reset({ ...defaultValues });
    }, [ defaultValues ]);

    const isFormDisabled = isCreating;
    return (
        <Modal backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>Create a New Project</Modal.Title>
            </ThemedModal.Header>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body ref={bodyAnimation}>
                        <ProjectDetailsForm isDisabled={isFormDisabled} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="success" type="submit" disabled={isFormDisabled}>Create Project</Button>
                        <Button variant="light" type="button" onClick={onClose} disabled={isFormDisabled}>Cancel</Button>
                    </Modal.Footer>
                </form>
            </FormProvider>
            
        </Modal>
    );
};

export function openCreateProjectModal(options: OpenCreateProjectModalOptions) : Promise<CreateProjectModalResponse> {
    return createConfirmation<CreateProjectModalProps, CreateProjectModalResponse>(confirmable(CreateProjectModal))({
        masterAccountId: options.masterAccountId,
        dialogId: 'create_project_modal',
        defaultValues: {
            divisionId: options.divisionId,
            masterAccountId: options.masterAccountId,
            description: '',
            comment: '',
            adviceType: options.adviceType ?? 4,
            status: options.status ?? 8,
            adviserId: options.adviserId,
            driverId: options.userId,
            targetDate: null,
            projectValue: 0,
            campaignId: null,
            campaignStatusId: null,
            campaignStatusDate: null
        }
    });
}